import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Lightbulb,
  RotateCcw,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Flame,
  Volume2,
  VolumeX,
  Megaphone,
  MegaphoneOff,
  Gem,
} from "lucide-react";
import confetti from "canvas-confetti";
import { Card } from "@/types/linguadeck";
import { useGamification } from "@/contexts/GamificationContext";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { playPop, playSuccess, playError } from "@/lib/studySfx";
import { useVictoryMusic } from "@/hooks/useVictoryMusic";
import { GameResultsScreen } from "@/components/study/GameResultsScreen";

const CRYSTALS_PER_CARD = 5;
const MAX_COMBO_MULTIPLIER = 5;

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Split answer into tokens: words by space; if single word, split into characters. */
function tokenizeAnswer(answer: string): string[] {
  const trimmed = (answer || "").trim();
  if (!trimmed) return [];
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length > 1) return words;
  if (words.length === 1 && words[0].length > 1) return words[0].split("");
  return words;
}

interface MemoryJumbleModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
}

/** Floating +N 💎 animation (spawns center of card, floats up, fades out). */
function FloatingCrystals({ amount, onComplete }: { amount: number; onComplete: () => void }) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-50"
      initial={false}
    >
      <motion.span
        className="inline-flex items-center gap-1.5 text-3xl font-bold text-[hsl(var(--accent2))] drop-shadow-lg whitespace-nowrap"
        initial={{ opacity: 1, y: 0, scale: 1 }}
        animate={{ opacity: 0, y: -80, scale: 1.2 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        onAnimationComplete={onComplete}
      >
        +{amount}
        <Gem className="w-8 h-8" aria-hidden />
      </motion.span>
    </motion.div>
  );
}

export default function MemoryJumbleMode({
  cards,
  lang: deckLang,
  onBack,
  onProgressComplete,
}: MemoryJumbleModeProps) {
  const { t: tFn } = useTranslation();
  const t = typeof tFn === "function" ? tFn : ((key: string, opts?: object) => key);
  const { addCrystals, finishStudySession } = useGamification();
  const {
    isSoundEnabled,
    isVoiceEnabled,
    gameVolume,
    voiceVolume,
    setGameVolume,
    setVoiceVolume,
    toggleSound,
    toggleVoice,
  } = useSoundStore();

  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );

  const [cardIndex, setCardIndex] = useState(0);
  const [shuffledTokens, setShuffledTokens] = useState<string[]>([]);
  const [correctTokens, setCorrectTokens] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [userSequence, setUserSequence] = useState<string[]>([]);
  const [showHint, setShowHint] = useState(false);
  const [wrongShake, setWrongShake] = useState(false);
  const [dropZoneFlash, setDropZoneFlash] = useState(false);
  const [successFlash, setSuccessFlash] = useState(false);
  const [combo, setCombo] = useState(0);
  const [floatingCrystals, setFloatingCrystals] = useState<number | null>(null);
  const [isGameFinished, setIsGameFinished] = useState(false);
  const [sessionCrystals, setSessionCrystals] = useState(0);

  const bgMusicRef = useRef<HTMLAudioElement | null>(null);

  useVictoryMusic({
    isGameFinished,
    isSoundEnabled,
    gameVolume,
    bgmAudioRef: bgMusicRef,
  });


  const currentCard = filteredCards[cardIndex] ?? null;
  const total = filteredCards.length;

  /** Target word = front (language being learned); hint = back (translation/meaning). */
  const correctOrder = useMemo(() => {
    if (!currentCard?.front) return [];
    return tokenizeAnswer(currentCard.front);
  }, [currentCard]);

  // ─── BGM: play study music while mode is active ─────────────────────────────
  useEffect(() => {
    const el = bgMusicRef.current;
    if (!el || filteredCards.length === 0) return;
    const vol = isSoundEnabled ? gameVolume : 0;
    el.volume = vol;
    if (isSoundEnabled) el.play().catch(() => {});
    return () => {
      el.pause();
      el.currentTime = 0;
    };
  }, [isSoundEnabled, gameVolume, filteredCards.length]);

  useEffect(() => {
    if (!currentCard || correctOrder.length === 0) return;
    setCorrectTokens(correctOrder);
    setShuffledTokens(shuffleArray(correctOrder));
    setSelectedIds([]);
    setUserSequence([]);
    setShowHint(false);
    setWrongShake(false);
    setDropZoneFlash(false);
    setSuccessFlash(false);
  }, [currentCard, cardIndex]);

  const remainingTokenIds = useMemo(() => {
    return shuffledTokens
      .map((_, id) => id)
      .filter((id) => !selectedIds.includes(id));
  }, [shuffledTokens, selectedIds]);

  const triggerWrongFeedback = useCallback(() => {
    setWrongShake(true);
    setDropZoneFlash(true);
    if (isSoundEnabled) playError(gameVolume * 0.8);
    setTimeout(() => setWrongShake(false), 400);
    setTimeout(() => setDropZoneFlash(false), 500);
    setCombo(0);
  }, [isSoundEnabled, gameVolume]);

  const handleSelectToken = useCallback(
    (tokenId: number) => {
      if (successFlash || showHint) return;
      const token = shuffledTokens[tokenId];
      if (!token) return;

      if (selectedIds.length === 0 && useSoundStore.getState().isVoiceEnabled && (currentCard?.front || "").trim()) {
        void playAudio({
          card: currentCard,
          side: "front",
          deckLang: currentCard.frontLang ?? deckLang,
          volume: voiceVolume,
        });
      }
      if (isSoundEnabled) playPop(gameVolume * 0.6);

      const nextSelected = [...selectedIds, tokenId];
      const nextSequence = [...userSequence, token];
      setSelectedIds(nextSelected);
      setUserSequence(nextSequence);

      if (nextSequence.length !== correctOrder.length) return;

      const isCorrect =
        nextSequence.length === correctOrder.length &&
        nextSequence.every((s, i) => s === correctOrder[i]);

      if (isCorrect) {
        setSuccessFlash(true);
        const comboMultiplier = Math.max(1, Math.min(combo + 1, MAX_COMBO_MULTIPLIER));
        const baseAmount = CRYSTALS_PER_CARD * comboMultiplier;
        const result = addCrystals(baseAmount, t("memoryJumble.rewardReason"), {
          isLearningAction: true,
          silent: true,
        });
        const totalAdd = typeof result === "object" ? result.totalAdd : baseAmount;
        setFloatingCrystals(totalAdd);
        setSessionCrystals((prev) => prev + totalAdd);
        setCombo((c) => c + 1);

        if (isSoundEnabled) playSuccess(gameVolume * 0.7);
        if (useSoundStore.getState().isVoiceEnabled && currentCard?.front?.trim()) {
          const frontLang = currentCard.frontLang ?? deckLang;
          setTimeout(
            () => void playAudio({
              card: currentCard,
              side: "front",
              deckLang: frontLang,
              volume: voiceVolume,
            }),
            300
          );
        }
        confetti({
          colors: ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b"],
          origin: { y: 0.7 },
          particleCount: 120,
          spread: 60,
        });
        setTimeout(() => {
          setFloatingCrystals(null);
          const nextIndex = cardIndex + 1;
          if (total > 0 && onProgressComplete) {
            const pct = Math.min(100, Math.round((nextIndex / total) * 100));
            onProgressComplete(pct);
          }
          if (cardIndex >= total - 1) {
            finishStudySession(total, false);
            setIsGameFinished(true);
          } else {
            setCardIndex((i) => i + 1);
          }
        }, 1200);
      } else {
        triggerWrongFeedback();
      }
    },
    [
      shuffledTokens,
      selectedIds,
      currentCard,
      userSequence,
      correctOrder,
      successFlash,
      showHint,
      cardIndex,
      total,
      addCrystals,
      finishStudySession,
      combo,
      t,
      isSoundEnabled,
      gameVolume,
      isVoiceEnabled,
      voiceVolume,
      currentCard,
      deckLang,
      triggerWrongFeedback,
      onProgressComplete,
    ]
  );

  const handleUndo = useCallback(() => {
    if (successFlash || showHint || selectedIds.length === 0) return;
    setSelectedIds((ids) => ids.slice(0, -1));
    setUserSequence((seq) => seq.slice(0, -1));
  }, [successFlash, showHint, selectedIds.length]);

  const handleTryAgain = useCallback(() => {
    if (successFlash) return;
    setCombo(0);
    setSelectedIds([]);
    setUserSequence([]);
    setShuffledTokens(shuffleArray(correctTokens));
    setWrongShake(false);
    setDropZoneFlash(false);
  }, [successFlash, correctTokens]);

  const handleShowHint = useCallback(() => {
    setShowHint(true);
    setCombo(0);
  }, []);

  const handlePlayAgain = useCallback(() => {
    setIsGameFinished(false);
    setSessionCrystals(0);
    setCardIndex(0);
    setCombo(0);
    setSelectedIds([]);
    setUserSequence([]);
    setShowHint(false);
    setWrongShake(false);
    setDropZoneFlash(false);
    setSuccessFlash(false);
  }, []);

  const handleBackToDeck = useCallback(() => {
    onBack?.();
  }, [onBack]);

  const headerRow = (
    <header className="flex justify-between items-center w-full max-w-2xl mx-auto mb-6 gap-4">
      <div className="min-w-0 flex-1">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium transition-colors"
            aria-label={t("studyMenu.backToDeck")}
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("studyMenu.backToDeck")}
          </button>
        ) : (
          <div />
        )}
      </div>
      <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={gameVolume}
          onChange={(e) => setGameVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.gameVolume")}
        />
        <button
          type="button"
          onClick={toggleSound}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isSoundEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={isSoundEnabled ? t("common.muteMusic") : t("common.unmuteMusic")}
        >
          {isSoundEnabled ? (
            <Volume2 className="w-5 h-5" />
          ) : (
            <VolumeX className="w-5 h-5" />
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voiceVolume}
          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.voiceVolume")}
        />
        <button
          type="button"
          onClick={toggleVoice}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={isVoiceEnabled ? t("common.voiceModeOn") : t("common.voiceModeOff")}
        >
          {isVoiceEnabled ? (
            <Megaphone className="w-5 h-5" />
          ) : (
            <MegaphoneOff className="w-5 h-5" />
          )}
        </button>
      </div>
    </header>
  );

  if (filteredCards.length === 0) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto px-4">
        {headerRow}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 text-center">
          <p className="text-lg font-medium text-[hsl(var(--foreground))]">
            {t("memoryJumble.needCards")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            {t("memoryJumble.addMoreCards")}
          </p>
        </div>
      </div>
    );
  }

  if (isGameFinished) {
    return (
      <div className="flex flex-col w-full max-w-2xl mx-auto px-4 pb-10 relative">
        <audio ref={bgMusicRef} src="/memory-jumble.mp3" loop preload="auto" />
        <GameResultsScreen
          sessionCrystals={sessionCrystals}
          onPlayAgain={handlePlayAgain}
          onBackToDeck={handleBackToDeck}
          headerRow={headerRow}
        />
      </div>
    );
  }

  const isSingleToken =
    correctOrder.length === 1 && (currentCard?.front ?? "").trim().length === 1;

  return (
    <div className="flex flex-col w-full max-w-2xl mx-auto px-4 pb-10 relative">
      <audio ref={bgMusicRef} src="/memory-jumble.mp3" loop preload="auto" />

      <AnimatePresence>
        {floatingCrystals != null && (
          <FloatingCrystals
            amount={floatingCrystals}
            onComplete={() => setFloatingCrystals(null)}
          />
        )}
      </AnimatePresence>

      {headerRow}

      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <span className="text-sm text-[hsl(var(--muted-foreground))]">
          {t("memory_level_info", "Level {{current}}/{{total}} • {{pairs}} pairs", {
            current: cardIndex + 1,
            total,
            pairs: total,
          })}
        </span>
        {combo > 1 && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 font-semibold text-sm border border-amber-500/40"
          >
            <Flame size={18} className="shrink-0" />
            {t("memoryJumble.combo", { count: combo, defaultValue: `Combo x${combo}!` })}
          </motion.div>
        )}
      </div>

      <motion.div
        className={`rounded-[32px] border-2 bg-white dark:bg-[hsl(var(--card-bg))] shadow-[0_20px_50px_rgba(0,0,0,0.1)] overflow-hidden transition-colors ${
          wrongShake ? "animate-shake border-red-500 dark:border-red-500/60" : "border-[hsl(var(--border))]"
        } ${successFlash ? "ring-2 ring-[hsl(var(--accent2))]" : ""}`}
      >
        <div className="p-6 md:p-8">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-2">
            {t("memoryJumble.meaningLabel", { defaultValue: t("memoryJumble.termLabel") })}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))] mb-6 break-words">
            {currentCard?.back}
          </p>

          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-3">
            {t("memoryJumble.reconstructHint")}
          </p>

          {showHint ? (
            <>
              <div className="flex flex-wrap gap-2 mb-4 p-4 rounded-xl bg-[hsl(var(--muted))]">
                {correctOrder.map((tkn, i) => (
                  <span
                    key={`hint-${i}-${tkn}`}
                    className="px-3 py-1.5 rounded-lg bg-[hsl(var(--card-bg))] text-[hsl(var(--foreground))] font-medium"
                  >
                    {tkn}
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setCardIndex((i) => (i + 1) % Math.max(1, total))}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))] transition-colors"
              >
                {t("memoryJumble.nextCard")}
                <ChevronRight size={18} />
              </button>
            </>
          ) : (
            <>
              <div
                className={`min-h-[52px] flex flex-wrap gap-2 p-4 rounded-xl border-2 border-dashed bg-[hsl(var(--muted)/0.3)] mb-4 ${
                  dropZoneFlash ? "animate-drop-zone-error" : "border-[hsl(var(--border))]"
                }`}
              >
                <AnimatePresence mode="popLayout">
                  {userSequence.map((tkn, i) => (
                    <motion.span
                      key={`sel-${i}-${tkn}`}
                      layout
                      initial={{ opacity: 0, scale: 0.5, y: 8 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-medium text-[15px]"
                    >
                      {tkn}
                    </motion.span>
                  ))}
                </AnimatePresence>
                {successFlash && (
                  <motion.span
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    className="inline-flex items-center gap-1.5 text-[hsl(var(--accent2))] font-semibold"
                  >
                    <CheckCircle2 size={20} />
                    {t("memoryJumble.correct")}
                  </motion.span>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {remainingTokenIds.map((tokenId) => {
                  const token = shuffledTokens[tokenId];
                  if (!token) return null;
                  return (
                    <motion.button
                      key={`${tokenId}-${token}`}
                      layout
                      type="button"
                      onClick={() => handleSelectToken(tokenId)}
                      disabled={successFlash}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.97 }}
                      className="px-4 py-2.5 rounded-xl font-medium text-[15px] border-2 border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:border-[hsl(var(--primary)/0.6)] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] transition-colors disabled:opacity-70 disabled:pointer-events-none"
                    >
                      {token}
                    </motion.button>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {selectedIds.length > 0 && !successFlash && (
                  <button
                    type="button"
                    onClick={handleUndo}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] text-sm font-medium transition-colors"
                  >
                    <RotateCcw size={16} />
                    {t("memoryJumble.undo")}
                  </button>
                )}
                {userSequence.length === correctOrder.length &&
                  userSequence.length > 0 &&
                  !successFlash && (
                    <button
                      type="button"
                      onClick={handleTryAgain}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 text-sm font-medium transition-colors"
                    >
                      <XCircle size={16} />
                      {t("memoryJumble.tryAgain")}
                    </button>
                  )}
                {!showHint && !successFlash && (
                  <button
                    type="button"
                    onClick={handleShowHint}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] text-sm font-medium transition-colors"
                  >
                    <Lightbulb size={16} />
                    {t("memoryJumble.showHint")}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>

      {isSingleToken && (
        <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] text-center">
          {t("memoryJumble.singleCharHint")}
        </p>
      )}
    </div>
  );
}
